import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { EMPTY, catchError, filter, tap } from 'rxjs';

import { CollectionFileProgressPopup } from '@/component/Collection';
import { useRemoveFileModal } from '@/hook/collection/useRemoveFileModal';
import { createSafeContext, useSubscriptions, useVModal } from '@/package';
import {
  useAuthStore,
  useCollectionManagementStore,
  useTenantStore,
} from '@/store';
import type { S3ListItem } from '@/type';

import { useWebSocket } from '../webSocket';

type CollectionContextValue = {
  isInitializedLoading: boolean;
  selectedItemList: S3ListItem[];
  setSelectedItemList: Dispatch<SetStateAction<S3ListItem[]>>;
  openRemoveConfirm: (onRemove?: () => void) => void;
};

export const CollectionContext = createSafeContext<CollectionContextValue>(
  {} as CollectionContextValue,
  'CollectionContext',
);

export const CollectionProvider = ({ children }: PropsWithChildren) => {
  const { alarmMessage$ } = useWebSocket();
  const { user, isTenantAuthenticated } = useAuthStore();
  const {
    connected,
    currentDirectoryUuid,
    currentBucket,
    downloadFileStatusMap,
    itemByUuid,
    isConnected,

    cancelUpload,
    clearDownloadFileStatusMap,
    clearUploadFileStatusMap,
    downloadFileList,
    getBucketList,
    getCurrentDir,
    moveRoot,
    readDirectory,
    setChildren,
    setCurrentBucket,
    setDownloadFileStatus,
    uploadFileStatusMap,
  } = useCollectionManagementStore();
  const enableCancelUpload = useMemo(
    () => Object.values(uploadFileStatusMap).some(m => m.status === 'pending'),
    [uploadFileStatusMap],
  );
  const { currentTenantID } = useTenantStore();
  const [selectedItemList, setSelectedItemList] = useState<S3ListItem[]>([]);
  const openRemoveConfirm = useRemoveFileModal();
  const { openAlert } = useVModal();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitializedLoading, setIsInitializedLoading] = useState(false);

  const uploadingItemList = useMemo(() => {
    const itemList = [];

    for (const key in uploadFileStatusMap) {
      const item = uploadFileStatusMap[key];
      const token = item.key.split('/');

      itemList.push({
        name: token[token.length - 1],
        status: item.status,
        isDir: false,
        error: item.error,
      });
    }

    return itemList;
  }, [uploadFileStatusMap]);
  const downloadingItemList = useMemo(() => {
    const itemList = [];

    for (const key in downloadFileStatusMap) {
      const item = downloadFileStatusMap[key];
      const token = item.key.replace(/\/$/, '').split('/');

      itemList.push({
        name: token[token.length - 1],
        status: item.status,
        isDir: item.isDir,
        error: item.error,
      });
    }

    return itemList;
  }, [downloadFileStatusMap]);
  const isReady = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => (currentTenantID ? isConnected() : false) && isTenantAuthenticated(),
    [connected, currentTenantID, user],
  );

  useEffect(() => {
    if (
      !location.pathname.match(/collection/) ||
      !isReady ||
      itemByUuid.root.uuid !== currentDirectoryUuid
    )
      return;

    moveRoot();
    readDirectory({ childrenOption: { autoAdd: true } })
      .catch(err => {
        if (err.data.resultCode == 3403 || err.data.resultCode == 3400) {
          openAlert({
            title: t('storage.goToLinkGuidePageTitle.elutriate'),
            noCloseOnBackdrop: true,
            noCloseOnEsc: true,
            message: () => (
              <div
                dangerouslySetInnerHTML={{
                  __html: t('storage.goToLinkGuidePageMessage.enostosis', {
                    confirmText: t('modal.confirmButtonText.vanilla'),
                  }),
                }}
              />
            ),
            confirmVariant: 'danger',
            confirmText: t('modal.confirmButtonText.vanilla'),
            size: 'small',
            onHide() {
              navigate('/collection/storage');
              setCurrentBucket(getBucketList()![0]);
            },
          });
        }
      })
      .finally(() => {
        setIsInitializedLoading(getCurrentDir().isLoading ?? false);
      });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsInitializedLoading(getCurrentDir().isLoading ?? true);
  }, [connected, currentBucket, currentDirectoryUuid, isReady, location, user]);

  useSubscriptions(
    () => [
      alarmMessage$
        .pipe(
          filter(payload => payload.alarmType === 'DownloadFolderZipDone'),
          tap(payload => {
            const keyList = Object.keys(downloadFileStatusMap);
            const filePath = payload.reportPayload?.filePath;
            const key = keyList.find(key =>
              filePath.includes(key.replace(/(^\/)|(\/$)/gi, '')),
            );

            if (!key) {
              throw new Error('not found key', { cause: filePath });
            }

            setDownloadFileStatus(key, {
              status: 'done',
              percent: 100,
            });

            downloadFileList([{ key: filePath, downloadFileName: key }]);
          }),
          catchError(error => {
            console.warn(error);

            if (error.cause.filePath) {
              setDownloadFileStatus(error.cause.filePath, {
                status: 'error',
                percent: 0,
              });
            }

            return EMPTY;
          }),
        )
        .subscribe(),

      alarmMessage$
        .pipe(
          filter(payload => payload.alarmType === 'MiriTaskDeleteObjectsDone'),
          tap(payload => {
            const reportPayload = payload.reportPayload as {
              folderPath: string;
              currentCount: number;
            };
            const removedKey = reportPayload.folderPath;
            const currentChildren = getCurrentDir().children ?? [];

            setChildren({
              children: currentChildren.filter(
                item => !removedKey.match(item.key),
              ),
            });
          }),
        )
        .subscribe(),
    ],
    [alarmMessage$, currentDirectoryUuid, downloadFileStatusMap],
  );

  return (
    <CollectionContext.Provider
      value={{
        isInitializedLoading,
        selectedItemList,
        setSelectedItemList,
        openRemoveConfirm,
      }}
    >
      {children}
      <CollectionFileProgressPopup
        downloadingItemList={downloadingItemList}
        enableCancelUpload={enableCancelUpload}
        uploadingItemList={uploadingItemList}
        clearDownloadFileStatusMap={clearDownloadFileStatusMap}
        clearUploadFileStatusMap={clearUploadFileStatusMap}
        onUploadCancel={cancelUpload}
      />
    </CollectionContext.Provider>
  );
};
